import { Component } from "@angular/core";
import templateString from "./billingPortalTemplate.html";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { Title } from "@angular/platform-browser";
import { StripeFactoryService, StripeInstance, Elements, Element as StripeElement, ElementsOptions } from "ngx-stripe";
import { BillingPortalService } from "./billingPortal.service";

// Sub-buyer billing portal (implementation_plans/sub_buyer_passthrough_billing.md §5.3).
// The Stripe card / Payment Element / micro-deposit flows are lifted from the builder's
// collectBillingDetails component; the difference is that every call carries the portal
// bearer token instead of a Devise session. Conventions: no optional chaining (Node 12),
// no inline styles (classes live in utility_app/billing_portal.scss).
@Component({
  selector: "billing-portal",
  template: templateString
})
export class BillingPortalComponent {
  constructor(
    private portal: BillingPortalService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private titleService: Title,
    private stripeFactory: StripeFactoryService
  ) {}

  // Session
  loading: boolean = true;
  invalidLink: boolean = false;
  session: any = null;
  tab: string = "payment_methods";

  // Billing contact
  contactForm: FormGroup;
  contactSaving: boolean = false;

  // Payment methods
  paymentMethods: any[] = [];
  stripe: StripeInstance;
  stripeJs: any;
  elements: Elements;
  card: StripeElement;
  elementsOptions: ElementsOptions = { locale: "auto" };
  cardSubmitting: boolean = false;
  showBankWidget: boolean = false;
  loadingSetupIntent: boolean = false;
  bankSubmitting: boolean = false;
  bankSetupIntentClientSecret: string = null;
  bankSetupIntentId: string = null;
  bankPaymentElement: any = null;
  bankElements: any = null;
  verifyPaymentMethod: any = null;
  bankMicroGroup: FormGroup;

  // Invoices
  invoices: any[] = [];
  invoiceDetail: any = null;
  invoicesLoaded: boolean = false;

  // The site nav is a shared, absolutely-positioned <header> whose links and logo are white:
  // every other utility page puts a dark hero behind it. This page's background is near-white
  // (#f7f8fb), so left alone the links render at roughly 1.05:1 contrast and the wordmark
  // disappears entirely. The header lives outside this component (a sibling of <router-outlet>),
  // so no descendant selector can reach it — mark the body while the portal is mounted and
  // restyle from there. See billing_portal.scss.
  static readonly BODY_CLASS: string = "billing-portal-page";

  ngOnInit() {
    document.body.classList.add(BillingPortalComponent.BODY_CLASS);
    this.titleService.setTitle("TheoremReach - Billing Portal");
    this.contactForm = this.fb.group({ billing_email: [""], billing_contact_name: [""] });
    this.bankMicroGroup = this.fb.group({ amount: [""], descriptorCode: [""] });

    window.addEventListener("hashchange", this.onHashChange);
    this.openFromLocation();
  }

  ngOnDestroy() {
    // Leaving it behind would restyle the nav on whatever page is navigated to next.
    document.body.classList.remove(BillingPortalComponent.BODY_CLASS);
    window.removeEventListener("hashchange", this.onHashChange);
  }

  // A fresh link opened in a tab already showing the portal changes only the fragment, which the
  // browser handles without reloading the page, so start over on the new token rather than ignore
  // it. The scrub below removes the fragment with replaceState, which fires no hashchange.
  private onHashChange = () => {
    if (BillingPortalService.tokenFromHash(window.location.hash)) {
      this.openFromLocation();
    }
  };

  private openFromLocation() {
    // Fragment first: a freshly opened link always wins over whatever this tab was holding.
    var token = BillingPortalService.tokenFromHash(window.location.hash);
    var fromLink = !!token;
    // Nothing in the URL means either a reload — the fragment is scrubbed as soon as it is read,
    // so a valid session has no token left in the address bar — or an arrival with no link at all.
    // The stored copy tells those apart instead of showing "invalid link" to someone who merely
    // pressed refresh.
    if (!token) token = BillingPortalService.storedToken();

    if (!token) {
      this.loading = false;
      this.invalidLink = true;
      return;
    }
    this.resetState();
    this.portal.setToken(token);
    // Only the link path puts anything in the address bar worth removing.
    if (fromLink) this.scrubTokenFromUrl();

    this.loadSession();
  }

  // Keep the token out of the address bar and browser history once we hold it.
  //
  // Deferred to the next task rather than called inline: on the hashchange path the browser is
  // still completing the fragment navigation, and a replaceState issued from inside the handler
  // is undone when it finishes — the token stays in the URL. jsdom has no session history to
  // undo it, so this reproduces only in a real browser (verified in Chrome 2026-09-16); the
  // Playwright case is the regression guard, not the Jest one.
  private scrubTokenFromUrl() {
    if (!window.history || !window.history.replaceState) return;

    setTimeout(function() {
      window.history.replaceState({}, document.title, window.location.pathname);
    }, 0);
  }

  // Drops everything the previous link loaded, so a new link never shows another session's data.
  private resetState() {
    this.loading = true;
    this.invalidLink = false;
    this.session = null;
    this.tab = "payment_methods";
    this.paymentMethods = [];
    this.invoices = [];
    this.invoiceDetail = null;
    this.invoicesLoaded = false;
    this.verifyPaymentMethod = null;
    this.cancelBankConnection();
    // A card element belongs to the Stripe instance that created it; the next session builds its own.
    if (this.card) {
      try { this.card.unmount(); } catch (_unused) {}
      this.card = null;
    }
  }

  loadSession() {
    this.portal.session().subscribe(
      (session) => {
        this.session = session;
        this.loading = false;
        // Lets the stored token expire itself rather than lingering until the server rejects it.
        this.portal.noteExpiry(session.expires_at);
        this.contactForm.patchValue({
          billing_email: session.sub_buyer.billing_email || "",
          billing_contact_name: session.sub_buyer.billing_contact_name || ""
        });
        this.refreshPaymentMethods();
        this.initStripe();
      },
      (error) => {
        this.loading = false;
        this.endSession();
      }
    );
  }

  selectTab(tab: string) {
    this.tab = tab;
    if (tab === "invoices" && !this.invoicesLoaded) {
      this.refreshInvoices();
    }
    if (tab === "payment_methods") {
      setTimeout(() => this.mountCardElement(), 0);
    }
  }

  buyerHeading(): string {
    if (!this.session || !this.session.sub_buyer) return "";
    var name = this.session.sub_buyer.name || "";
    return this.session.company_name ? name + " (via " + this.session.company_name + ")" : name;
  }

  pausedForNonPayment(): boolean {
    return !!(this.session && this.session.sub_buyer && this.session.sub_buyer.state === "disabled_for_non_payment");
  }

  // The link is the session and it expires without warning; say when.
  linkExpiresAt(): Date {
    return this.session && this.session.expires_at ? new Date(this.session.expires_at) : null;
  }

  // The link is the session: a 401 after the page loaded means it expired or the master revoked
  // it, so the page drops what it holds and shows the state a dead link lands on rather than
  // reporting errors over stale data. Any other failure is reported and the page stays usable.
  private reportError(error: any, fallback: string, title: string) {
    if (error && error.status === 401) {
      this.endSession();
      return;
    }
    this.toastr.error(this.readErr(error) || fallback, title);
  }

  private endSession() {
    this.invalidLink = true;
    this.session = null;
    this.portal.setToken(null);
    this.cancelBankConnection();
  }

  // ---- Billing contact -----------------------------------------------------

  saveBillingContact() {
    if (this.contactSaving) return;
    var value = this.contactForm.value;
    if (!value.billing_email) {
      this.toastr.error("A billing email is required.", "Billing Contact");
      return;
    }
    this.contactSaving = true;
    this.portal.updateBillingContact({
      billing_email: value.billing_email,
      billing_contact_name: value.billing_contact_name
    }).subscribe(
      (response) => {
        this.session.sub_buyer.billing_email = response.billing_email;
        this.session.sub_buyer.billing_contact_name = response.billing_contact_name;
        this.contactSaving = false;
        this.toastr.success("", "Billing Contact Saved");
      },
      (error) => {
        this.contactSaving = false;
        this.reportError(error, "Could not save billing contact.", "Billing Contact");
      }
    );
  }

  // ---- Stripe setup ---------------------------------------------------------

  private initStripe() {
    var key = BillingPortalService.stripePublishableKey();
    if (!key) {
      this.toastr.error("Payments are not configured. Please contact support.", "Stripe");
      return;
    }
    this.stripe = this.stripeFactory.create(key);
    this.stripe.elements(this.elementsOptions).subscribe((elements) => {
      this.elements = elements;
      var stripeAny: any = this.stripe;
      var raw: any = null;
      if (typeof stripeAny.getInstance === "function") {
        raw = stripeAny.getInstance();
      }
      if (!raw && (elements as any)._stripe) {
        raw = (elements as any)._stripe;
      }
      if (!raw && (window as any).Stripe) {
        raw = (window as any).Stripe(key);
      }
      this.stripeJs = raw;
      setTimeout(() => this.mountCardElement(), 0);
    });
  }

  // The payment-methods section is *ngIf'd, so leaving the tab destroys the host node
  // while Stripe still considers the element mounted; re-entering must unmount first or
  // Stripe rejects the second mount and the card form renders empty.
  private mountCardElement() {
    if (!this.elements || this.tab !== "payment_methods") return;
    var host = document.getElementById("portal-card-element");
    if (!host) return;
    if (!this.card) {
      this.card = this.elements.create("card", {});
    } else {
      try {
        this.card.unmount();
      } catch (_unused) {
        // Not mounted anywhere yet; nothing to detach.
      }
    }
    this.card.mount("#portal-card-element");
  }

  // ---- Card flow -------------------------------------------------------------

  saveCard() {
    if (!this.stripeJs) {
      this.toastr.error("Stripe is not ready yet — please reload the page.", "Failed To Add Card");
      return Promise.resolve();
    }
    if (this.cardSubmitting) return Promise.resolve();
    this.cardSubmitting = true;

    return this.stripeJs.createPaymentMethod({ type: "card", card: this.card })
      .then((result: any) => {
        if (result.paymentMethod) {
          return this.saveNewPaymentMethod("card", { payment_method_id: result.paymentMethod.id })
            .then(() => { if (this.card) { this.card.clear(); } });
        } else if (result.error) {
          this.toastr.error(result.error.message || "Card details rejected by Stripe", "Failed To Add Card");
        } else {
          this.toastr.error("Unexpected response from Stripe", "Failed To Add Card");
        }
      })
      .catch((err: any) => {
        this.toastr.error(this.readErr(err) || "Could not reach Stripe — check your connection and try again.", "Failed To Add Card");
      })
      .then(() => { this.cardSubmitting = false; });
  }

  // ---- Bank flow (Payment Element) -------------------------------------------

  startBankConnection() {
    this.loadingSetupIntent = true;
    this.portal.createSetupIntent().subscribe(
      (response) => {
        this.bankSetupIntentClientSecret = response.client_secret;
        this.bankSetupIntentId = response.setup_intent_id;
        this.showBankWidget = true;
        this.loadingSetupIntent = false;
        setTimeout(() => this.mountBankPaymentElement(), 0);
      },
      (error) => {
        this.loadingSetupIntent = false;
        this.reportError(error, "Could not start bank connection", "Stripe");
      }
    );
  }

  private mountBankPaymentElement() {
    if (!this.stripeJs) {
      this.toastr.error("Stripe is not ready yet — please reload the page.", "Bank Connection");
      this.cancelBankConnection();
      return;
    }
    try {
      this.bankElements = this.stripeJs.elements({ clientSecret: this.bankSetupIntentClientSecret });
      this.bankPaymentElement = this.bankElements.create("payment");
      this.bankPaymentElement.mount("#portal-payment-element");
    } catch (err) {
      this.toastr.error(this.readErr(err) || "Could not load the bank connection widget.", "Bank Connection");
      this.cancelBankConnection();
    }
  }

  cancelBankConnection() {
    if (this.bankPaymentElement) {
      try { this.bankPaymentElement.unmount(); } catch (_unused) {}
      this.bankPaymentElement = null;
    }
    this.bankElements = null;
    this.showBankWidget = false;
    this.bankSetupIntentClientSecret = null;
    this.bankSetupIntentId = null;
  }

  confirmBankSetup() {
    if (!this.stripeJs) {
      this.toastr.error("Stripe is not ready yet — please reload the page.", "Bank Connection");
      return Promise.resolve();
    }
    if (this.bankSubmitting) return Promise.resolve();
    this.bankSubmitting = true;

    return this.stripeJs.confirmSetup({ elements: this.bankElements, redirect: "if_required" })
      .then((result: any) => {
        if (result.error) {
          this.toastr.error(result.error.message || "Bank account rejected", "Bank Connection Failed");
          return;
        }
        var setupIntent = result.setupIntent;
        if (!setupIntent || !setupIntent.payment_method) {
          this.toastr.error("Unexpected response from Stripe", "Bank Connection Failed");
          return;
        }
        var data: any = { payment_method_id: setupIntent.payment_method };
        if (setupIntent.status !== "succeeded") {
          data.setup_intent_id = this.bankSetupIntentId;
        }
        return this.saveNewPaymentMethod("bank", data).then(() => { this.cancelBankConnection(); });
      })
      .catch((err: any) => {
        this.toastr.error(this.readErr(err) || "Could not reach Stripe — check your connection and try again.", "Bank Connection Failed");
      })
      .then(() => { this.bankSubmitting = false; });
  }

  // ---- Payment method list ---------------------------------------------------

  saveNewPaymentMethod(type: string, data: any): Promise<any> {
    return new Promise<any>((resolve) => {
      this.portal.createPaymentMethod(type, data).subscribe(
        (response) => {
          this.refreshPaymentMethods();
          this.loadSessionQuietly();
          this.toastr.success("", "Payment Method Added");
          resolve(response);
        },
        (error) => {
          if (error && error.status === 409) {
            this.toastr.warning(this.readErr(error) || "This payment method is already on file.", "Already Added");
          } else {
            this.reportError(error, "Could not save payment method. Please try again.", "Cannot Create Payment Method");
          }
          resolve(null);
        }
      );
    });
  }

  refreshPaymentMethods() {
    this.portal.paymentMethods().subscribe(
      (response) => { this.paymentMethods = response; },
      (error) => { this.reportError(error, "Could not load payment methods.", "Payment Methods"); }
    );
  }

  // A card change can clear the hold server-side; refresh the banner without disturbing the
  // rest of the page. Only a dead link is acted on.
  private loadSessionQuietly() {
    this.portal.session().subscribe(
      (session) => { this.session = session; },
      (error) => { if (error && error.status === 401) { this.endSession(); } }
    );
  }

  makeDefault(paymentMethodId: number) {
    this.portal.makeDefault(paymentMethodId).subscribe(
      () => { this.refreshPaymentMethods(); this.loadSessionQuietly(); this.toastr.success("Payment Method Set to Default"); },
      (error) => { this.reportError(error, "Could not change default payment method.", "Cannot Set Default"); }
    );
  }

  deletePaymentMethod(paymentMethodId: number) {
    this.portal.deletePaymentMethod(paymentMethodId).subscribe(
      () => { this.refreshPaymentMethods(); this.toastr.success("Payment Method Removed!"); },
      (error) => { this.reportError(error, "Could not remove payment method.", "Cannot Remove Payment Method"); }
    );
  }

  startMicroDepositVerification(paymentMethod: any) {
    this.verifyPaymentMethod = paymentMethod;
    this.bankMicroGroup.reset();
  }

  cancelMicroDepositVerification() {
    this.verifyPaymentMethod = null;
  }

  processMicroDepositVerification() {
    if (!this.verifyPaymentMethod) return;
    var payload: any = {};
    var descriptorCode = this.bankMicroGroup.value.descriptorCode;
    var amount = this.bankMicroGroup.value.amount;
    if (descriptorCode) {
      payload.descriptor_code = descriptorCode;
    } else if (amount) {
      payload.amount = amount;
    } else {
      this.toastr.error("Please enter the descriptor code or amount from your bank statement.", "Verification");
      return;
    }
    this.portal.verifyMicroDeposits(this.verifyPaymentMethod.id, payload).subscribe(
      () => { this.toastr.success("Successfully verified bank account!"); this.verifyPaymentMethod = null; this.refreshPaymentMethods(); },
      (error) => { this.reportError(error, "Verification failed — double-check the value and try again.", "Failed Verification"); if (!this.invalidLink) { this.refreshPaymentMethods(); } }
    );
  }

  // ---- Invoices ------------------------------------------------------------------

  refreshInvoices() {
    this.portal.invoices().subscribe(
      (response) => { this.invoices = response; this.invoicesLoaded = true; },
      (error) => { this.reportError(error, "Could not load invoices.", "Invoices"); }
    );
  }

  expandInvoice(invoice: any) {
    this.portal.invoice(invoice.id).subscribe(
      (response) => { this.invoiceDetail = response; },
      (error) => { this.reportError(error, "Could not load invoice.", "Invoices"); }
    );
  }

  downloadInvoice(invoice: any): Promise<void> {
    return this.portal.downloadInvoice(invoice.id).then((blob) => {
      var url = window.URL.createObjectURL(blob);
      var link = document.createElement("a");
      link.href = url;
      link.download = "invoice-" + invoice.id + ".pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }).catch((err) => {
      this.reportError(err, "Could not download invoice.", "Invoices");
    });
  }

  readErr(e: any): string {
    try {
      if (!e) return null;
      var inner = e.error;
      if (inner) {
        if (inner.errors) {
          if (Array.isArray(inner.errors)) return inner.errors.join("; ");
          if (typeof inner.errors === "string") return inner.errors;
        }
        if (inner.error && typeof inner.error === "string") return inner.error;
        if (inner.message && typeof inner.message === "string") return inner.message;
      }
      if (e.message && typeof e.message === "string") return e.message;
      if (e.statusText && typeof e.statusText === "string") return e.statusText;
      return null;
    } catch (_unused) {
      return null;
    }
  }
}
